/**
 * stance-card.js — ONE CARD, TWO SURFACES.
 *
 * `signalStance` answers the same question about an open trade and about a portfolio holding, and
 * the card that shows the answer must be the same card. It was written inside `trades.js` first
 * (`2337cd9`) and lifted here the moment the holdings surface needed it — before the second copy
 * existed, not after, which is the only version of that decision that costs nothing.
 *
 * ⚠ THE SUBJECT IS PASSED IN, NOT INFERRED. A trade's subtitle is `Long · TRY`; a holding's is
 * `Stock · Garanti`; a holding can also carry a cross-reference chip because **ASELS is a holding
 * AND an open trade** and the same unresolved 30 July SELL sits against both. The card renders
 * what it is given and knows about neither model.
 *
 * ⚠ AND IT RENDERS NO CONTROL. No button, no `data-action`, nothing that writes — his rule of
 * 2026-08-16, *surface the contradiction, never act on it*, after watching one source reverse
 * itself inside ten days on the only ticker it had an opinion about. Tests on both surfaces
 * assert the absence.
 *
 * (2026-08-16)
 */

import { esc } from '../core/utils.js';
import { actionDirection } from '../core/signal-stance.js';

/** The five verdicts and the single place their labels and classes are written. */
export const STANCE_VERDICTS = {
  conflicted: { cls: 'conf', label: 'CONFLICTED' },
  superseded: { cls: 'sup', label: 'CALL STOOD DOWN' },
  agrees: { cls: 'agr', label: 'AGREES' },
  nocall: { cls: 'nc', label: 'NO DIRECTIONAL CALL' },
  unreadable: { cls: 'nc', label: 'SIDE NOT RECOGNISED' },
};

/** Conflicted first: the one state worth interrupting him for. */
export const STANCE_RANK = { conflicted: 0, superseded: 1, agrees: 2, nocall: 3, unreadable: 4 };

/**
 * The order both panels sort in. Verdict, then **how many calls are open against him**, then the
 * ticker so the answer is stable.
 *
 * The middle tier is not decoration: TUPRS carries **two** unresolved SELLs and ASELS one, and on
 * an alphabetical sort ASELS came first. The row with more of the tape against it is the row to
 * read first.
 */
export function stanceOrder(a, b) {
  return (STANCE_RANK[a.st.verdict] - STANCE_RANK[b.st.verdict])
    || (b.st.opposing.length - a.st.opposing.length)
    || String(a.ticker || '').localeCompare(String(b.ticker || ''));
}

const _dt = s => esc(String((s && s.sourceDate) || String((s && s.createdAt) || '').slice(0, 10) || '—'));
const _src = s => esc(String((s && s.sourceName) || 'unknown source'));
const _act = (s) => {
  const a = String((s && s.action) || '');
  return `<span class="stn-act ${actionDirection(a) || 'unk'}">${esc(a || '—')}</span>`;
};

/**
 * @param {Object} st      a `signalStance` result — never null; the caller drops those
 * @param {Object} subject { ticker, sub, chip?, sideNote? }
 *                 sub      the small grey line under the ticker
 *                 chip     optional extra chip HTML, rendered before the verdict
 *                 sideNote what to say when the verdict is 'unreadable'
 */
export function stanceCard(st, subject) {
  const s = subject || {};
  const v = STANCE_VERDICTS[st.verdict] || STANCE_VERDICTS.nocall;
  const lines = [];

  if (st.verdict === 'unreadable') {
    lines.push(`<div class="stn-l">${esc(s.sideNote || 'This position has no readable side, so nothing here can be called for or against it.')} The tape is shown as-is.</div>`);
  }

  if (st.latestDir) {
    lines.push(`<div class="stn-l"><b>${_src(st.latestDir)}</b> last called ${_act(st.latestDir)} on <b>${_dt(st.latestDir)}</b>`
      + `${st.latestDir.targetLevel != null ? `, target ${esc(String(st.latestDir.targetLevel))}` : ''}.</div>`);
  } else {
    lines.push(`<div class="stn-l">${st.count} report${st.count === 1 ? '' : 's'}, <b>none of them directional</b>`
      + ` — the latest is ${_act(st.latest)} on <b>${_dt(st.latest)}</b>.</div>`);
  }

  if (st.supersededBy.length) {
    const n = st.supersededBy.length;
    lines.push(`<div class="stn-l warn">⚠ ${_src(st.supersededBy[0])} has said `
      + `${_act(st.supersededBy[0])} ${n === 1 ? 'since' : n + ' times since'}`
      + `, most recently <b>${_dt(st.supersededBy[0])}</b>.</div>`);
  }

  st.opposing.forEach(o => {
    lines.push(`<div class="stn-l warn">⚠ ${_src(o)} called ${_act(o)} on <b>${_dt(o)}</b>`
      + `${o.targetLevel != null ? ` at ${esc(String(o.targetLevel))}` : ''}, and that call is still <b>${esc(String(o.status || 'open'))}</b>.</div>`);
  });

  // ⚠ SAY WHAT WAS NOT UNDERSTOOD. An action this build has never been told about is dropped from
  // the verdict, and a dropped row that nothing mentions is silent degradation.
  if (st.unknown) {
    lines.push(`<div class="stn-l mute">${st.unknown} call${st.unknown === 1 ? '' : 's'} with an action this build does not recognise — not counted either way.</div>`);
  }

  return `<div class="stn-card ${v.cls}">
    <div class="stn-h"><span class="tk" style="width:26px;height:26px;font-size:9px">${esc(String(s.ticker || '').slice(0, 4))}</span>
      <span class="stn-tk">${esc(s.ticker || '')}</span>
      <span class="stn-side">${esc(s.sub || '')}</span>
      ${s.chip || ''}
      <span class="stn-v ${v.cls}">${v.label}</span></div>
    ${lines.join('')}
    <div class="stn-drv"><span><i>calls</i>${st.count}</span>`
    + `<span><i>sources</i>${st.sources}</span>`
    + `${st.first && st.last && st.first !== st.last ? `<span><i>window</i>${esc(st.first)} → ${esc(st.last)}</span>` : ''}`
    + `<span><i>open against you</i>${st.opposing.length}</span></div>
  </div>`;
}
